// src/lib/rateLimiter.ts

interface RateLimitEntry {
  count: number;
  resetTime: number;
}

const rateLimitStore = new Map<string, RateLimitEntry>();

interface RateLimitOptions {
  maxAttempts: number;
  windowMs: number; // time window in milliseconds
  identifier: string; // unique identifier (email, IP, etc.)
}

/**
 * Rate limiter to prevent brute force attacks
 * 
 * @example
 * const isAllowed = checkRateLimit({
 *   maxAttempts: 5,
 *   windowMs: 15 * 60 * 1000, // 15 minutes
 *   identifier: email
 * });
 * 
 * if (!isAllowed) {
 *   throw new Error('Too many attempts. Please try again later.');
 * }
 */
export function checkRateLimit({ maxAttempts, windowMs, identifier }: RateLimitOptions): boolean {
  const now = Date.now();
  const key = identifier;

  // Get or create rate limit entry
  let entry = rateLimitStore.get(key);

  // Reset if time window has passed
  if (!entry || now > entry.resetTime) {
    entry = {
      count: 1,
      resetTime: now + windowMs
    };
    rateLimitStore.set(key, entry);
    return true;
  }

  // Increment attempt count
  entry.count++;

  // Check if limit exceeded
  if (entry.count > maxAttempts) {
    return false;
  }

  return true;
}

/**
 * Get remaining time until rate limit resets (in seconds)
 */
export function getRateLimitResetTime(identifier: string): number {
  const entry = rateLimitStore.get(identifier);
  if (!entry) return 0;

  const now = Date.now();
  const remainingMs = entry.resetTime - now;
  
  return Math.max(0, Math.ceil(remainingMs / 1000));
}

/**
 * Manually reset rate limit for an identifier
 */
export function resetRateLimit(identifier: string): void {
  rateLimitStore.delete(identifier);
}

/**
 * Clean up expired rate limit entries (call periodically)
 */
export function cleanupRateLimits(): void {
  const now = Date.now();
  for (const [key, entry] of rateLimitStore.entries()) {
    if (now > entry.resetTime) {
      rateLimitStore.delete(key);
    }
  }
}

// Clean up every 5 minutes
if (typeof window !== 'undefined') {
  setInterval(cleanupRateLimits, 5 * 60 * 1000);
}

/**
 * Rate limit configuration presets
 */
export const RateLimitPresets = {
  // Sign-in: 5 attempts per 15 minutes
  signIn: {
    maxAttempts: 5,
    windowMs: 15 * 60 * 1000
  },
  
  // Sign-up: 3 attempts per hour
  signUp: {
    maxAttempts: 3,
    windowMs: 60 * 60 * 1000
  },
  
  // Password reset: 3 attempts per hour
  passwordReset: {
    maxAttempts: 3,
    windowMs: 60 * 60 * 1000
  },
  
  // General API: 100 requests per minute
  api: {
    maxAttempts: 100,
    windowMs: 60 * 1000
  }
};
